import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useBookings } from "../../hooks";
import { useSEO } from "../../hooks";
import { bookingAPI } from "../../services/api";
import { format } from "date-fns";
import toast from "react-hot-toast";
import resolveImage from "../../utils/imageUrl";
import {
  FiCalendar,
  FiClock,
  FiMapPin,
  FiChevronRight,
  FiPlus,
  FiPackage,
} from "react-icons/fi";
import "./Dashboard.css";

const STATUS_TABS = [
  "all",
  "pending",
  "confirmed",
  "in_progress",
  "completed",
  "cancelled",
];

const CustomerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useSEO({
    title: `My Bookings — ${user?.name || "Dashboard"}`,
    description:
      "Manage your home service bookings on Expert At Door. View, track and cancel appointments.",
    canonical: "/dashboard",
    noindex: true,
  });

  const [activeTab, setActiveTab] = useState(
    location.pathname === "/dashboard/bookings" ? "pending" : "all",
  );
  const [cancellingId, setCancellingId] = useState(null);
  const [allBookings, setAllBookings] = useState([]);

  // Filtered list drives the visible table
  const params = activeTab !== "all" ? { status: activeTab } : {};
  const { bookings, loading, refetch } = useBookings(params);

  // Separate fetch for accurate stats (always all bookings)
  useEffect(() => {
    bookingAPI
      .getAll({ limit: 1000 })
      .then(({ data }) => setAllBookings(data.data || []))
      .catch(() => {});
  }, []);

  const handleCancel = async (bookingId, bookingNumber) => {
    if (!window.confirm(`Cancel booking ${bookingNumber}?`)) return;
    setCancellingId(bookingId);
    try {
      await bookingAPI.cancel(bookingId, "Cancelled by customer");
      toast.success("Booking cancelled.");
      refetch();
      bookingAPI
        .getAll({ limit: 1000 })
        .then(({ data }) => setAllBookings(data.data || []))
        .catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to cancel");
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <div className="dashboard-page">
      <div className="container">
        {/* Welcome header */}
        <div className="dashboard-header">
          <div className="dashboard-welcome">
            <div className="welcome-avatar">
              {user?.profileImage ? (
                <img src={resolveImage(user.profileImage)} alt={user.name} />
              ) : (
                <span>{user?.name?.[0]}</span>
              )}
            </div>
            <div>
              <h2>Hello, {user?.name?.split(" ")[0]} 👋</h2>
              <p>{user?.email}</p>
            </div>
          </div>

          <div className="dashboard-quick-actions">
            <button
              className="btn btn-primary"
              onClick={() => navigate("/services")}
            >
              <FiPlus size={16} /> Book a Service
            </button>
          </div>
        </div>

        {/* Quick stats */}
        <div className="dashboard-stats">
          {[
            {
              label: "Total Bookings",
              value: allBookings.length,
              icon: <FiPackage />,
            },
            {
              label: "Active",
              value: allBookings.filter((b) =>
                ["pending", "confirmed", "in_progress"].includes(b.status),
              ).length,
              icon: <FiClock />,
            },
            {
              label: "Completed",
              value: allBookings.filter((b) => b.status === "completed").length,
              icon: "✅",
            },
          ].map(({ label, value, icon }) => (
            <div key={label} className="stat-card">
              <span className="stat-icon">{icon}</span>
              <div>
                <span className="stat-value">{value}</span>
                <span className="stat-label">{label}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Bookings */}
        <div className="dashboard-section">
          <h3 className="section-title-sm">My Bookings</h3>

          <div className="status-tabs">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab}
                className={`status-tab ${activeTab === tab ? "active" : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab === "all" ? "All" : tab.replace("_", " ")}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="bookings-list">
              {Array(3)
                .fill(0)
                .map((_, i) => (
                  <div
                    key={i}
                    className="booking-card skeleton"
                    style={{ height: 120 }}
                  />
                ))}
            </div>
          ) : bookings.length === 0 ? (
            <div className="empty-state">
              <span className="empty-icon">📋</span>
              <h4>No bookings yet</h4>
              <p>Book your first service to see it here</p>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => navigate("/services")}
              >
                Browse Services
              </button>
            </div>
          ) : (
            <div className="bookings-list">
              {bookings.map((booking) => (
                <BookingCard
                  key={booking._id}
                  booking={booking}
                  onCancel={handleCancel}
                  cancellingId={cancellingId}
                  navigate={navigate}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const BookingCard = ({ booking, onCancel, cancellingId, navigate }) => {
  const canCancel = ["pending", "confirmed"].includes(booking.status);

  return (
    <div className="booking-card">
      <div className="booking-card-left">
        <div className="booking-service-img">
          {booking.serviceId?.image ? (
            <img
              src={resolveImage(booking.serviceId.image)}
              alt={booking.serviceId?.name}
            />
          ) : (
            <span>🔧</span>
          )}
        </div>
        <div className="booking-info">
          <div className="booking-top-row">
            <h4>{booking.serviceSnapshot?.name || booking.serviceId?.name}</h4>
            <span className={`badge badge-${booking.status}`}>
              {booking.status.replace("_", " ")}
            </span>
          </div>
          <p className="booking-number">#{booking.bookingNumber}</p>
          <div className="booking-meta-row">
            <span className="booking-meta-item">
              <FiCalendar size={12} />
              {format(new Date(booking.scheduledDate), "dd MMM yyyy")}
            </span>
            <span className="booking-meta-item">
              <FiClock size={12} /> {booking.scheduledTimeSlot}
            </span>
            <span className="booking-meta-item">
              <FiMapPin size={12} /> {booking.address?.city}
            </span>
          </div>
          {booking.technicianId && (
            <p className="booking-tech">
              Expert: <strong>{booking.technicianId?.name}</strong>
              {booking.technicianId?.phone &&
                ` · ${booking.technicianId.phone}`}
            </p>
          )}
        </div>
      </div>

      <div className="booking-card-right">
        <span className="booking-amount">₹{booking.totalAmount}</span>
        <span className={`badge badge-${booking.paymentStatus}`}>
          {booking.paymentStatus}
        </span>

        <div className="booking-actions">
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => navigate(`/booking/${booking._id}`)}
          >
            Details <FiChevronRight size={14} />
          </button>
          {canCancel && (
            <button
              className="btn btn-sm"
              style={{
                color: "var(--status-error)",
                border: "1px solid var(--status-error)",
              }}
              disabled={cancellingId === booking._id}
              onClick={() => onCancel(booking._id, booking.bookingNumber)}
            >
              {cancellingId === booking._id ? "Cancelling..." : "Cancel"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDashboard;
